import type { EditorAction } from "../../editor/reducers";
import type { Dispatch } from "react";
import type { ArtifactRegionCreationTabFragment } from "../../client/generated";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { NavbarTabCloseButton } from "../../shell/NavbarTabCloseButton";
import { NavbarTabContent } from "../../shell/NavbarTabContent";
import { useTranslation } from "react-i18next";
import { makeTabIdentifier } from "../../editor/reducers";
import { useState } from "react";
import { assertTypeName } from "../../client/guards";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { PaginationArtifactFormField } from "../../attributes/pagination-artifact/PaginationArtifactFormField";
import {
  PaginationArtifactType,
  useArtifactRegionCreationTabSuspenseQuery,
  useCreateArtifactRegionMutation,
} from "../../client/generated";

type Props = {
  fragment: ArtifactRegionCreationTabFragment;
  dispatch: Dispatch<EditorAction>;
};

export function ArtifactRegionCreationTab({ fragment, dispatch }: Props) {
  const { t } = useTranslation();

  return (
    <NavbarTabContent
      value={makeTabIdentifier("artifact", fragment.id)}
      title={t("ArtifactRegionCreationTab.title")}
      right={<NavbarTabCloseButton dispatch={dispatch} />}
    >
      <ScrollArea className="flex-1">
        <Body fragment={fragment} dispatch={dispatch} />
      </ScrollArea>
    </NavbarTabContent>
  );
}

function Body({ fragment, dispatch }: Props) {
  const paginationArtifactAttributeIds = fragment.attributes
    .filter((attr) => attr.__typename === "PaginationArtifactAttribute")
    .map((attr) => attr.id);

  const { data } = useArtifactRegionCreationTabSuspenseQuery({
    variables: {
      regionId: fragment.id,
      paginationArtifactAttributeIds,
    },
  });

  assertTypeName(data.region?.__typename === "Region");

  const [createRegion] = useCreateArtifactRegionMutation();

  const [type, setType] = useState(() => {
    const attr = data.paginationArtifactAttributes[0];
    if (!attr) return undefined;
    assertTypeName(attr.__typename === "PaginationArtifactAttribute");
    return attr.type;
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (type === undefined) return false;

    const result = await createRegion({
      variables: {
        regionId: fragment.id,
        type,
        excessAttributeIds: fragment.attributes.map((attr) => attr.id),
      },
      update: (cache) => {
        for (const attr of fragment.attributes) {
          const id = cache.identify(attr);
          cache.evict({ id });
          cache.gc();
        }
      },
    });

    return (
      !result.errors &&
      !result.data?.createPaginationArtifactAttribute.userErrors?.length &&
      !result.data?.deleteAttributes.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <PaginationArtifactFormField
        value={type}
        onValueChange={(newType: PaginationArtifactType) => setType(newType)}
      />
    </RegionUpdateForm>
  );
}
